import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { useBedbankSelectDatesWithFlights } from 'hooks/OfferPage/useBedbankSelectDates'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { addToCheckoutCart, checkoutSetRoomsConnected, initBedbankSession, initialiseCheckout, validateOfferDiscount } from 'actions/CheckoutActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { FlightViewTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { generateBedbankAccommodationCheckoutItems } from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { dateDifference } from 'lib/datetime/dateUtils'
import { createBedbankHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/bedbankHotel'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import * as Analytics from 'analytics/analytics'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import BedbankChangePriceModal from 'components/OfferPage/Bedbank/Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { addToUserCart } from 'actions/UserCartActions'
import { getSource } from 'selectors/offerSelectors'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { fetchAccumulatedRatesForBedbankOffer, updateBedbankPricingData } from 'actions/BedbankOfferActions'
import invariant from 'tiny-invariant'
import { sum, uniqueBy } from 'lib/array/arrayUtils'
import { serialiseOccupancy } from 'lib/search/searchUtils'
import type { AppDispatch } from 'src/client/store'
import { generateInsuranceItem } from 'lib/checkout/insurance/cart'
import { INSURANCE_PRODUCT_ID } from 'constants/insurance'

type SessionResponse = Awaited<ReturnType<typeof initBedbankSession>>

function isValidSession(session: SessionResponse): session is Extract<SessionResponse, { sessionId: string }> {
  return !!('sessionId' in session)
}

interface ChangedRate {
  rate: App.BedbankRate
  newPrice?: number
  newLuxPlusPrice?: number
  newPropertyFees?: number
  rooms: Array<App.Occupants>
}

function calculatePriceChange(changedRates: Array<ChangedRate>, isLuxPlusBooking: boolean): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const oldDisplayPrice = sum(changedRates, ch => {
    if (isLuxPlusBooking && ch.rate.totals.memberInclusive) {
      return ch.rate.totals.memberInclusive
    }
    return ch.rate.totals.inclusive
  })
  const newDisplayPrice = sum(changedRates, ch => {
    const newLuxPlusPrice = ch.newLuxPlusPrice ?? ch.rate.totals.memberInclusive ?? 0
    if (isLuxPlusBooking && newLuxPlusPrice) {
      return newLuxPlusPrice
    }
    return ch.newPrice ?? ch.rate.totals.inclusive
  })
  const oldPropertyFees = sum(changedRates, ch => ch.rate.totals.propertyFees ?? 0)
  const newPropertyFees = sum(changedRates, ch => ch.newPropertyFees ?? ch.rate.totals.propertyFees ?? 0)
  return { oldDisplayPrice, newDisplayPrice, oldPropertyFees, newPropertyFees }
}

/**
 * Dispatches an action to patch changed rates in the offer store and returns semi-patched rates
 * that can be used to perform a new bedbank service enquiry request.
 */
function updateChangedRates(dispatch: AppDispatch, { offerId, checkIn, checkOut, changedRates }: {
  offerId: string
  checkIn: string
  checkOut: string
  changedRates: Array<ChangedRate>
}): Array<App.BedbankRate> {
  return changedRates.map(({
    rate,
    newPrice,
    newLuxPlusPrice,
    newPropertyFees,
    rooms,
  }) => {
    const resolvedPrice = newPrice ?? rate.totals.inclusive
    const resolvedLuxPlusPrice = newLuxPlusPrice ?? rate.totals.memberInclusive ?? 0
    const resolvedPropertyFees = newPropertyFees ?? rate.totals.propertyFees ?? 0

    if (
      resolvedPrice === rate.totals.inclusive &&
      resolvedLuxPlusPrice === (rate.totals.memberInclusive ?? 0) &&
      resolvedPropertyFees === (rate.totals.propertyFees ?? 0)
    ) {
      // Not all rates have necessarily changed.
      return rate
    }

    dispatch(updateBedbankPricingData(
      offerId,
      rate.roomId,
      rate.id,
      rate.isFlightBundle,
      checkIn,
      checkOut,
      rooms,
      // NB: The price change error only tells us the total, not the room-level changes. To work
      // around this, we estimate by dividing by the number of rooms.
      {
        data: [{
          inclusive: resolvedPrice / rooms.length,
          memberInclusive: resolvedLuxPlusPrice / rooms.length,
          propertyFees: resolvedPropertyFees / rooms.length,
        }],
      },
    ))

    // We don't need to be as thorough here; we only need to patch the fields that're used to
    // perform a bedbank service enquiry request.
    const newRate: App.BedbankRate = {
      ...rate,
      totals: {
        ...rate.totals,
        inclusive: resolvedPrice,
        memberInclusive: resolvedLuxPlusPrice,
        propertyFees: resolvedPropertyFees,
      },
    }

    return newRate
  })
}

function refreshSingleRoomRates({ dispatch, offerId, rooms, checkInDate, checkOutDate }: {
  dispatch: AppDispatch
  offerId: string
  rooms: Array<App.Occupants>
  checkInDate: string
  checkOutDate: string
}) {
  dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, rooms, checkInDate, checkOutDate, true))
}

function refreshMultiRoomRates({ dispatch, offerId, roomsWithData, checkInDate, checkOutDate }: {
  dispatch: AppDispatch
  offerId: string
  roomsWithData: Array<RoomWithData>
  checkInDate: string
  checkOutDate: string
}) {
  const uniqueRooms = uniqueBy(roomsWithData.map(({ room }) => room), room => serialiseOccupancy([room]))
  uniqueRooms.forEach(room => {
    dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, [room], checkInDate, checkOutDate, true))
  })
}

interface SingleRoomArgs {
  offer: App.BedbankOffer
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  rooms: Array<OfferPageRoom>
  originAirport?: App.Airport
  isLoggedIn?: boolean
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  rateListId: string
  roomPosition: number
  roomListId: string
  flightPrice?: number
  connectRooms: boolean
}

export function useBedbankSingleRoomBookNowCallback({
  offer,
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  rateListId,
  roomPosition,
  roomListId,
  flightPrice,
  connectRooms,
}: SingleRoomArgs) {
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const tripPlannerTripId = useAppSelector(state => state.tripPlanner.currentTripId)
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)
  const onBook = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
    keepBrowsing = false,
    isInsuranceSelected?: boolean,
    discountValue?: number,
  ) => {
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal
          message={intl.formatMessage({
            id: 'UCznyyUL7pToKg3_BrQl',
            defaultMessage: 'Please wait...',
            description: 'Processing modal message while bedbank booking is being processed',
          })}
          fullHeight
        />
      })

      const pkg = packageView.package

      if (currentTrip) {
        const payload = createBedbankHotelItemPayload({
          offer,
          pkg,
          rate,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          currentRegionCode,
          currencyCode: currency,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }

      try {
        // Initiate a session
        const session = await initBedbankSession(dispatch, {
          propertyId: offer.id,
          roomTypeId: rate.roomId,
          roomRateId: rate.id,
          rateGroupId: rate.groupId,
          bedGroupId: rate.bedGroups[0].id,
          isFlightBundle: rate.isFlightBundle,
          csBooking: !!isSpoofed,
          region: currentRegionCode,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          price: rate.totals.inclusive,
          propertyFees: rate.totals.propertyFees,
          memberInclusive: rate.totals.memberInclusive,
          subscriber,
          source,
        })
        if (discountValue) {
          dispatch(validateOfferDiscount({
            offerId: offer.id,
            region: currentRegionCode,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            occupancy: sumUpOccupancies(rooms).toString(),
            discount: discountValue.toString(),
            fkRoomTypeId: rate.roomId,
            roomRateId: rate.id,
          }))
        }

        closeProcessingModal()
        if (!isValidSession(session)) {
          const changedRates = [{
            rate,
            newPrice: session.price,
            newLuxPlusPrice: session.luxplusPrice,
            newPropertyFees: session.propertyFees,
            rooms,
          }]
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            // try book again with the updated rate
            onBook(newRates[0], ratePosition, withFlights, keepBrowsing)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
        }

        if (isValidSession(session)) {
          const masterBedGroupDescriptions = pkg.bedGroupDescriptions
          const accommodationItems = generateBedbankAccommodationCheckoutItems(
            offer,
            pkg,
            rate,
            {
              checkIn: checkInDate,
              checkOut: checkOutDate,
              duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
            },
            rooms,
            session.sessionId,
            undefined, // bundledItemIds
            masterBedGroupDescriptions,
          )

          const flightItems: Array<App.Checkout.FlightItem> = []
          if (withFlights && originAirport) {
            const flightOccupants = countOccupantsForFlights(rooms)

            flightItems.push(generateFlightCheckoutItem({
              originAirportCode: originAirport?.code,
              destinationAirportCode: offer.flights.airportCode!,
              fareType: 'return',
              viewType: FlightViewTypes.TWO_ONE_WAYS,
              occupants: flightOccupants,
              passengers: generatePassengersFromOccupants(flightOccupants),
              quotedFare: flightPrice,
              bundledItemIds: accommodationItems.map(item => item.itemId),
            }))
          }

          const insuranceItems: Array<App.Checkout.InsuranceItem> = []
          if (isInsuranceSelected) {
            const bedbankRoomIds = accommodationItems.map(item => item.roomId)
            const insuranceItem = generateInsuranceItem({ product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' }, customCoverage: rate.totals.total, bedbankRoomIds, isAccommodationBundle: true })
            insuranceItems.push(insuranceItem)
            Analytics.trackClientEvent({
              subject: 'flex_cfmr_package',
              action: 'add_to_cart',
              type: 'interaction',
              category: 'insurance',
            })
          }

          dispatch(addToCheckoutCart([...accommodationItems, ...flightItems, ...insuranceItems]))
          dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))

          fireEcommerceGTMEvent(addToCartGTM(offer, currency, session.price, sumUpOccupancies(rooms), 'hotel'))
          Analytics.trackEvent(addToCart({
            type: 'bedbank',
            ratePosition,
            rateListId,
            roomPosition,
            roomListId,
            rate,
            isFlightBundle: !!withFlights,
          }))

          if (keepBrowsing) {
            showSnackbar(intl.formatMessage({
              id: 'nY4_exwST6mDs_OWpjKs',
              defaultMessage: 'Your item has been added to the cart.',
              description: 'Snackbar message after adding a bedbank item to the cart',
            }), 'success')
            return
          }

          dispatch(navigateToCheckout())
        }
      } catch(_error) {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
        showModal(<BedbankSoldOutModal />)
      }
    }
  }, [checkInDate, checkOutDate, connectRooms, createTripItems, currency, currentRegionCode, currentTrip, dispatch, flightPrice, intl, isLoggedIn, isSpoofed, offer, openDateSelector, originAirport, packageView.package, rateListId, roomListId, roomPosition, rooms, showModal, source, subscriber, trackEvent])

  return onBook
}

export function useBedbankSingleRoomAddToUserCartCallback({
  offer,
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  rateListId,
  roomPosition,
  roomListId,
  flightPrice,
  connectRooms,
}: SingleRoomArgs) {
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const tripPlannerTripId = useAppSelector(state => state.tripPlanner.currentTripId)
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)

  const onAddToUserCart = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
    isInsuranceSelected?: boolean,
  ) => {
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      const pkg = packageView.package

      if (currentTrip) {
        const payload = createBedbankHotelItemPayload({
          offer,
          pkg,
          rate,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          currentRegionCode,
          currencyCode: currency,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }

      const masterBedGroupDescriptions = pkg.bedGroupDescriptions
      const accommodationItems = generateBedbankAccommodationCheckoutItems(
        offer,
        pkg,
        rate,
        {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
        },
        rooms,
        undefined, // sessionId
        undefined, // bundledItemIds
        masterBedGroupDescriptions,
      )
      const flightItems: Array<App.Checkout.FlightItem> = []
      if (withFlights && originAirport) {
        const flightOccupants = countOccupantsForFlights(rooms)

        flightItems.push(generateFlightCheckoutItem({
          originAirportCode: originAirport?.code,
          destinationAirportCode: offer.flights.airportCode!,
          fareType: 'return',
          viewType: FlightViewTypes.TWO_ONE_WAYS,
          occupants: flightOccupants,
          passengers: generatePassengersFromOccupants(flightOccupants),
          quotedFare: flightPrice,
          bundledItemIds: accommodationItems.map(item => item.itemId),
        }))
      }

      const insuranceItems: Array<App.Checkout.InsuranceItem> = []
      if (isInsuranceSelected) {
        const bedbankRoomIds = accommodationItems.map(item => item.roomId)
        const insuranceItem = generateInsuranceItem({ product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' }, customCoverage: rate.totals.total, bedbankRoomIds, isAccommodationBundle: true })
        insuranceItems.push(insuranceItem)
        Analytics.trackClientEvent({
          subject: 'flex_cfmr_package',
          action: 'add_to_cart',
          type: 'interaction',
          category: 'insurance',
        })
      }

      fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.value, sumUpOccupancies(rooms), 'hotel'))

      const checkoutItems = [...accommodationItems, ...flightItems, ...insuranceItems]
      if (flightItems.length) {
        let closeProcessingModal: any
        showModal((resolve) => {
          closeProcessingModal = resolve
          return <ProcessingModal
            message={intl.formatMessage({
              id: 'UCznyyUL7pToKg3_BrQl',
              defaultMessage: 'Please wait...',
              description: 'Processing modal message while bedbank booking is being processed',
            })}
            fullHeight
          />
        })

        // Initiate a session
        const session = await initBedbankSession(dispatch, {
          propertyId: offer.id,
          roomTypeId: rate.roomId,
          roomRateId: rate.id,
          rateGroupId: rate.groupId,
          isFlightBundle: rate.isFlightBundle,
          csBooking: !!isSpoofed,
          region: currentRegionCode,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          price: rate.totals.inclusive,
          propertyFees: rate.totals.propertyFees,
          memberInclusive: rate.totals.memberInclusive,
          subscriber,
          source,
        })

        closeProcessingModal()
        if (!isValidSession(session)) {
          const changedRates = [{
            rate,
            newPrice: session.price,
            newLuxPlusPrice: session.luxplusPrice,
            newPropertyFees: session.propertyFees,
            rooms,
          }]
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            onAddToUserCart(newRates[0], ratePosition, withFlights)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
        }

        if (isValidSession(session)) {
          // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
          dispatch(initialiseCheckout([...accommodationItems.map(item => ({ ...item, sessionId: session.sessionId })), ...flightItems, ...insuranceItems], { mode: 'vertical-select' }))
          dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))
          dispatch(navigateToCheckout({ skipSpoofedModal: true }))
        }
      } else {
        const bedbankContextData = {
          ratePosition,
          rateListId,
          roomPosition,
          roomListId,
          rate,
          isFlightBundle: !!withFlights,
        }
        Analytics.trackEvent(addToCartMultiItem(checkoutItems, { isMultiCart: true, bedbankContextData }))
        dispatch(addToUserCart(
          checkoutItems,
          false, // skipNotification
          connectRooms,
        ))
      }
    }
  }, [isSpoofed, source, subscriber, checkInDate, checkOutDate, connectRooms, createTripItems, currency, currentRegionCode, currentTrip, dispatch, flightPrice, intl, isLoggedIn, offer, openDateSelector, originAirport, packageView.package, rateListId, roomListId, roomPosition, rooms, showModal, trackEvent])

  return onAddToUserCart
}

export interface RoomWithData {
  room: OfferPageRoom
  package: App.BedbankPackage
  rate: App.BedbankRate
  bundleRate: App.BedbankRate
  /** Either the normal or bundle rate, depending on whether flights are selected */
  effectiveRate: App.BedbankRate
  promotion?: App.BedbankPromotion
  capacityValid: boolean
}

interface MultiRoomArgs {
  offer: App.BedbankOffer
  roomsWithData: Array<RoomWithData> | undefined
  checkInDate?: string
  checkOutDate?: string
  originAirport?: App.Airport
  isLoggedIn?: boolean
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  flightPrice?: number
  connectRooms: boolean
  withFlights: boolean
}

export function useBedbankMultiRoomBookNowCallback({
  offer,
  roomsWithData: initialRoomsWithData,
  checkInDate,
  checkOutDate,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  flightPrice,
  connectRooms,
  withFlights,
}: MultiRoomArgs) {
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const openDateSelector = useBedbankSelectDatesWithFlights(initialRoomsWithData?.[0]?.package.roomId)
  const onBook = useCallback(async(
    keepBrowsing = false,
    isInsuranceSelected: boolean = false,
    roomsWithData: Array<RoomWithData> | undefined = initialRoomsWithData,
  ) => {
    if (!roomsWithData) {
      return
    }
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }

      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal
          message={intl.formatMessage({
            id: 'UCznyyUL7pToKg3_BrQl',
            defaultMessage: 'Please wait...',
            description: 'Processing modal message while bedbank booking is being processed',
          })}
          fullHeight
        />
      })

      try {
        const roomSessions = await Promise.all(roomsWithData.map(async(roomData) => {
          const rate = roomData.effectiveRate

          // Initiate a session
          const session = await initBedbankSession(dispatch, {
            propertyId: offer.id,
            roomTypeId: rate.roomId,
            roomRateId: rate.id,
            rateGroupId: rate.groupId,
            bedGroupId: rate.bedGroups[0].id,
            isFlightBundle: rate.isFlightBundle,
            csBooking: !!isSpoofed,
            region: currentRegionCode,
            rooms: [roomData.room],
            checkIn: checkInDate,
            checkOut: checkOutDate,
            price: rate.totals.inclusive,
            propertyFees: rate.totals.propertyFees,
            memberInclusive: rate.totals.memberInclusive,
            subscriber,
            source,
          })

          return {
            roomData,
            session,
          }
        }))

        closeProcessingModal()

        if (roomSessions.some(rs => !('sessionId' in rs.session))) {
          // CHANGE OF PRICE - CANNOT PROCEED UNTIL USER ACCEPTS PRICE

          roomSessions
            .filter(rs => !isValidSession(rs.session))
            .forEach(roomSession => {
              const session = roomSession.session
              invariant(!isValidSession(session))
              const rate = roomSession.roomData.effectiveRate
              const room = roomSession.roomData.room
              dispatch(updateBedbankPricingData(
                offer.id,
                rate.roomId,
                rate.id,
                rate.isFlightBundle,
                checkInDate,
                checkOutDate,
                [room],
                {
                  data: [{
                    inclusive: session.price,
                    memberInclusive: session.luxplusPrice,
                    propertyFees: session.propertyFees,
                  }],
                },
              ))
            })

          const changedRates = roomSessions.map(rs => ({
            rate: rs.roomData.effectiveRate,
            newPrice: rs.session.price,
            newLuxPlusPrice: rs.session.luxplusPrice,
            newPropertyFees: 'propertyFees' in rs.session ? rs.session.propertyFees : undefined,
            rooms: [rs.roomData.room],
          }))
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            // try book again with the updated rate
            const newRoomsWithData = roomsWithData.map<RoomWithData>((rd, i) => (
              withFlights ?
                  { ...rd, bundleRate: newRates[i], effectiveRate: newRates[i] } :
                  { ...rd, rate: newRates[i], effectiveRate: newRates[i] }
            ))
            onBook(keepBrowsing, false, newRoomsWithData)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
        } else {
          // ALL GOOD, LET'S DO THIS!
          const rooms = roomsWithData.map(rd => rd.room)

          const accommodationItems = roomSessions.map(rs => {
            const session = rs.session
            invariant(isValidSession(session)) // We already checked that all sessions are valid and therefore have `sessionId`s
            const masterBedGroupDescriptions = rs.roomData.package.bedGroupDescriptions
            return (
              generateBedbankAccommodationCheckoutItems(
                offer,
                rs.roomData.package,
                rs.roomData.effectiveRate,
                {
                  checkIn: checkInDate,
                  checkOut: checkOutDate,
                  duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
                },
                [rs.roomData.room],
                session.sessionId,
                undefined, // bundledItemIds
                masterBedGroupDescriptions,
              )
            )
          }).flat()

          const flightItems: Array<App.Checkout.FlightItem> = []
          if (withFlights && originAirport) {
            const flightOccupants = countOccupantsForFlights(rooms)

            flightItems.push(generateFlightCheckoutItem({
              originAirportCode: originAirport?.code,
              destinationAirportCode: offer.flights.airportCode!,
              fareType: 'return',
              viewType: FlightViewTypes.TWO_ONE_WAYS,
              occupants: flightOccupants,
              passengers: generatePassengersFromOccupants(flightOccupants),
              quotedFare: flightPrice,
              bundledItemIds: accommodationItems.map(item => item.itemId),
            }))
          }

          const insuranceItems: Array<App.Checkout.InsuranceItem> = []
          if (isInsuranceSelected) {
            const bedbankRoomIds = accommodationItems.map(item => item.roomId)
            const totalCoverage = sum(roomsWithData, room => room.effectiveRate.totals.total)
            const insuranceItem = generateInsuranceItem({
              product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' },
              customCoverage: totalCoverage,
              bedbankRoomIds,
              isAccommodationBundle: true,
            })
            insuranceItems.push(insuranceItem)
            Analytics.trackClientEvent({
              subject: 'flex_cfmr_package',
              action: 'add_to_cart',
              type: 'interaction',
              category: 'insurance',
            })
          }

          dispatch(addToCheckoutCart([...accommodationItems, ...flightItems, ...insuranceItems]))
          dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))
          fireEcommerceGTMEvent(addToCartGTM(offer, currency, sum(roomSessions, rs => rs.session.price ?? 0), sumUpOccupancies(rooms), 'hotel'))

          if (keepBrowsing) {
            showSnackbar(intl.formatMessage({
              id: 'nY4_exwST6mDs_OWpjKs',
              defaultMessage: 'Your item has been added to the cart.',
              description: 'Snackbar message after adding a bedbank item to the cart',
            }), 'success')
            return
          }

          dispatch(navigateToCheckout())
        }
      } catch(_error) {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
        showModal(<BedbankSoldOutModal />)
      }
    }
  }, [checkInDate, checkOutDate, connectRooms, currency, currentRegionCode, dispatch, flightPrice, initialRoomsWithData, intl, isLoggedIn, isSpoofed, offer, openDateSelector, originAirport, showModal, source, subscriber, trackEvent, withFlights])

  return onBook
}

export function useBedbankMultiRoomAddToUserCartCallback({
  offer,
  roomsWithData: initialRoomsWithData,
  checkInDate,
  checkOutDate,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  flightPrice,
  connectRooms,
  withFlights,
}: MultiRoomArgs) {
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const source = useAppSelector(getSource)
  const intl = useIntl()

  const openDateSelector = useBedbankSelectDatesWithFlights(initialRoomsWithData?.[0]?.package.roomId)

  const onAddToUserCart = useCallback(async(
    roomsWithData: Array<RoomWithData> | undefined = initialRoomsWithData,
    isInsuranceSelected: boolean = false,
  ) => {
    if (!roomsWithData) { return undefined }
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')
      if (!isLoggedIn && !offer.walledGarden) {
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
        if (!result.loggedIn) {
          return
        }
      }
      const rooms = roomsWithData.map(rd => rd.room)

      const accommodationItems = roomsWithData.map(rd => generateBedbankAccommodationCheckoutItems(
        offer,
        rd.package,
        rd.effectiveRate,
        {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
        },
        [rd.room],
        undefined, // sessionId
        undefined, // bundledItemIds
        rd.package.bedGroupDescriptions,
      )).flat()

      const flightItems: Array<App.Checkout.FlightItem> = []
      if (withFlights && originAirport) {
        const flightOccupants = countOccupantsForFlights(rooms)

        flightItems.push(generateFlightCheckoutItem({
          originAirportCode: originAirport?.code,
          destinationAirportCode: offer.flights.airportCode!,
          fareType: 'return',
          viewType: FlightViewTypes.TWO_ONE_WAYS,
          occupants: flightOccupants,
          passengers: generatePassengersFromOccupants(flightOccupants),
          quotedFare: flightPrice,
          bundledItemIds: accommodationItems.map(item => item.itemId),
        }))
      }

      const insuranceItems: Array<App.Checkout.InsuranceItem> = []
      if (isInsuranceSelected) {
        const bedbankRoomIds = accommodationItems.map(item => item.roomId)
        const totalCoverage = sum(roomsWithData, room => room.effectiveRate.totals.total)
        const insuranceItem = generateInsuranceItem({
          product: { id: INSURANCE_PRODUCT_ID.CFMR, type: 'cfmr' },
          customCoverage: totalCoverage,
          bedbankRoomIds,
          isAccommodationBundle: true,
        })
        insuranceItems.push(insuranceItem)
        Analytics.trackClientEvent({
          subject: 'flex_cfmr_package',
          action: 'add_to_cart',
          type: 'interaction',
          category: 'insurance',
        })
      }

      fireEcommerceGTMEvent(addToCartGTM(offer, currency, sum(roomsWithData, rd => rd.effectiveRate.value), sumUpOccupancies(rooms), 'hotel'))
      const checkoutItems = [...accommodationItems, ...flightItems, ...insuranceItems]
      if (flightItems.length) {
        let closeProcessingModal: any
        showModal((resolve) => {
          closeProcessingModal = resolve
          return <ProcessingModal
            message={intl.formatMessage({
              id: 'UCznyyUL7pToKg3_BrQl',
              defaultMessage: 'Please wait...',
              description: 'Processing modal message while bedbank booking is being processed',
            })}
            fullHeight
          />
        })

        // Initiate sessions
        const roomSessions = await Promise.all(roomsWithData.map(async(roomData) => {
          const rate = roomData.effectiveRate

          const session = await initBedbankSession(dispatch, {
            propertyId: offer.id,
            roomTypeId: rate.roomId,
            roomRateId: rate.id,
            rateGroupId: rate.groupId,
            isFlightBundle: rate.isFlightBundle,
            csBooking: !!isSpoofed,
            region: currentRegionCode,
            rooms: [roomData.room],
            checkIn: checkInDate,
            checkOut: checkOutDate,
            price: rate.totals.inclusive,
            propertyFees: rate.totals.propertyFees,
            memberInclusive: rate.totals.memberInclusive,
            subscriber,
            source,
          })

          return {
            roomData,
            session,
          }
        }))

        closeProcessingModal()

        if (roomSessions.some(rs => !('sessionId' in rs.session))) {
          // CHANGE OF PRICE - CANNOT PROCEED UNTIL USER ACCEPTS PRICE

          roomSessions
            .filter(rs => !isValidSession(rs.session))
            .forEach(rs => {
              const session = rs.session
              invariant(!isValidSession(session))
              const rate = rs.roomData.effectiveRate
              const room = rs.roomData.room
              dispatch(updateBedbankPricingData(
                offer.id,
                rate.roomId,
                rate.id,
                rate.isFlightBundle,
                checkInDate,
                checkOutDate,
                [room],
                {
                  data: [{
                    inclusive: session.price,
                    memberInclusive: session.luxplusPrice,
                    propertyFees: session.propertyFees,
                  }],
                },
              ))
            })

          const changedRates = roomSessions.map(rs => ({
            rate: rs.roomData.effectiveRate,
            newPrice: rs.session.price,
            newLuxPlusPrice: rs.session.luxplusPrice,
            newPropertyFees: 'propertyFees' in rs.session ? rs.session.propertyFees : undefined,
            rooms: [rs.roomData.room],
          }))
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            // try book again with the updated rate
            const newRoomsWithData = roomsWithData.map<RoomWithData>((rd, i) => (
              withFlights ?
                  { ...rd, bundleRate: newRates[i], effectiveRate: newRates[i] } :
                  { ...rd, rate: newRates[i], effectiveRate: newRates[i] }
            ))
            onAddToUserCart(newRoomsWithData)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshMultiRoomRates({ dispatch, offerId: offer.id, roomsWithData, checkInDate, checkOutDate })
        } else {
          // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
          dispatch(initialiseCheckout([
            ...accommodationItems.map((item, i) => {
              const session = roomSessions[i].session
              invariant(isValidSession(session))
              return ({ ...item, sessionId: session.sessionId })
            }),
            ...flightItems,
            ...insuranceItems,
          ], { mode: 'vertical-select' }))
          dispatch(checkoutSetRoomsConnected(offer.id, connectRooms))
          dispatch(navigateToCheckout({ skipSpoofedModal: true }))
        }
      } else {
        dispatch(addToUserCart(
          checkoutItems,
          false, // skipNotification
          connectRooms,
        ))
      }
    }
  }, [initialRoomsWithData, checkInDate, checkOutDate, connectRooms, openDateSelector, trackEvent, isLoggedIn, offer, originAirport, currency, showModal, flightPrice, dispatch, intl, isSpoofed, currentRegionCode, subscriber, source, withFlights])

  return onAddToUserCart
}
